import {
  AnimatedContainer,
  AnimatedSection
} from '@components/animated'
import { FloatingSave } from '@components/form/FloatingSave'
import { Labeled } from '@components/form/Labeled'
import { Section } from '@components/index'
import { AccessControl } from '@components/layout'
import { SectionTitle } from '@components/section/SectionTitle'
import { useCountries } from '@services/common/useCountries'
import {
  AlertTriangle,
  Briefcase,
  Contact,
  Info,
  User as UserIcon,
  Users
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { UserDetail, UserProfile } from 'types/api'

import { Combobox } from '@/components/ui/combobox'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'

import { formatIdNumber, ID_PATTERN, PASSPORT_PATTERN } from '../idFormats'

import {
  CountryField,
  DegreeField,
  JobField,
  NationalityField
} from './UsersFormSelects'

function ageFromIso(iso?: string | null) {
  if (!iso) return null
  const b = new Date(iso)
  if (isNaN(b.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - b.getFullYear()
  const m = now.getMonth() - b.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--
  return age
}

// Which section each form field belongs to (for auto-expand on validation error).
const FIELD_SECTION: Record<string, string> = {
  first_name: 'personal',
  last_name: 'personal',
  sex: 'personal',
  birth_date: 'personal',
  nationality: 'personal',
  country: 'personal',
  id_number: 'personal',
  passport_number: 'personal',
  degree: 'personal',
  rating: 'personal',
  email: 'contact',
  phone: 'contact',
  street: 'contact',
  address_number: 'contact',
  address_orientation_number: 'contact',
  zip: 'contact',
  city: 'contact',
  facebook: 'contact',
  instagram: 'contact',
  linkedin: 'contact',
  job: 'business',
  income: 'business'
}

// --- Temporarily hidden profile fields (may come back) — flip to re-enable ---
const SHOW_SOCIALS = false

const TODAY_ISO = new Date().toISOString().slice(0, 10)
// Latest birth date that still makes someone a legal adult (18+).
const ADULT_BIRTH_DATE_MAX_ISO = (() => {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 18)
  return d.toISOString().slice(0, 10)
})()

const PHONE_PATTERN = /^(?:\+|00)[1-9]\d{0,2}\d{6,12}$/
const ZIP_PATTERN =
  /^\d{5}(-\d{4})?$|^[A-Z0-9]{3} [A-Z0-9]{3}$|^[A-Z0-9]{2}-\d{4}$/

type UsersFormProps = {
  data?: UserDetail | UserProfile
  profilePhoto?: string
  uploadPhoto: ({ body }: { body: FormData }) => void
  submitForm: (values: UserDetail) => void
  /** Hide the employment ("Pracovné údaje") section — e.g. for landlords. */
  hideEmployment?: boolean
}

export function UsersForm({
  data,
  submitForm,
  hideEmployment = false
}: UsersFormProps) {
  const { t } = useTranslation('users')
  const [showFloatingSave, setShowFloatingSave] = useState(false)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    personal: true,
    legal: true,
    contact: true,
    business: true
  })
  const { data: countries } = useCountries({ search: '' })

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    getValues,
    formState: { errors }
  } = useForm<UserDetail>({
    mode: 'onTouched',
    defaultValues: (data as UserDetail) ?? undefined
  })

  const sectionOpenProps = (key: string) => ({
    open: openSections[key],
    onOpenChange: (o: boolean) =>
      setOpenSections((s) => ({ ...s, [key]: o }))
  })

  const selectedCountryId = watch('country')
  const selectedLegalRepCountryId = watch('legal_representative.country')
  const birthDate = watch('birth_date')

  const isUnder18 = useMemo(() => {
    const age = ageFromIso(birthDate)
    return age !== null && age < 18
  }, [birthDate])

  const countryIsSlovakia = (id?: string | null) => {
    if (!id) return false
    const c = countries?.find((country) => country.id === id)
    const name = c?.name?.toLowerCase()
    return name === 'slovensko' || name === 'slovakia'
  }
  const isSlovakia = useMemo(
    () => countryIsSlovakia(selectedCountryId),
    [selectedCountryId, countries]
  )
  const isLegalRepSlovakia = useMemo(
    () => countryIsSlovakia(selectedLegalRepCountryId),
    [selectedLegalRepCountryId, countries]
  )

  // Passport is required only for a selected non-Slovak country; in every
  // other case (Slovakia or no country picked yet) the birth number is.
  const passportRequired = !!selectedCountryId && !isSlovakia
  const legalRepPassportRequired =
    !!selectedLegalRepCountryId && !isLegalRepSlovakia

  // Re-validate id/passport when the country changes OR once the countries list
  // loads and `isSlovakia` resolves — otherwise an error raised before the list
  // was available stays stuck even after we know the citizen is Slovak.
  useEffect(() => {
    if (selectedCountryId !== undefined) {
      trigger(['id_number', 'passport_number']).catch(() => {})
    }
  }, [selectedCountryId, isSlovakia, trigger])

  useEffect(() => {
    if (selectedLegalRepCountryId !== undefined) {
      trigger([
        'legal_representative.id_number',
        'legal_representative.passport_number'
      ]).catch(() => {})
    }
  }, [selectedLegalRepCountryId, isLegalRepSlovakia, trigger])

  // Show the floating save bar on any user-driven change.
  useEffect(() => {
    const sub = watch((_, { type }) => {
      if (type === 'change') setShowFloatingSave(true)
    })
    return () => sub.unsubscribe()
  }, [watch])

  function discardChanges() {
    if (data) reset(data as UserDetail)
    setShowFloatingSave(false)
  }

  function onValid(values: UserDetail) {
    setShowFloatingSave(false)
    submitForm(values)
  }

  function onInvalid(formErrors: Record<string, unknown>) {
    setOpenSections((prev) => {
      const next = { ...prev }
      Object.keys(formErrors).forEach((key) => {
        if (key === 'legal_representative') next.legal = true
        else {
          const sec = FIELD_SECTION[key]
          if (sec) next[sec] = true
        }
      })
      return next
    })
  }

  if (!data) {
    return null
  }

  const lenRule =
    (min: number, max: number) =>
    (v?: string | null) =>
      !v || (v.length >= min && v.length <= max) || t('form.validation.required')

  const invalidCls = (e?: unknown) => (e ? 'border-destructive' : '')

  // Render the contract note with its inline "*" highlighted in red.
  const noteText = t('form.contractRequiredNote')
  const noteStarIdx = noteText.indexOf('*')
  const contractNote =
    noteStarIdx === -1 ? (
      noteText
    ) : (
      <>
        {noteText.slice(0, noteStarIdx)}
        <span className="font-semibold text-destructive">*</span>
        {noteText.slice(noteStarIdx + 1)}
      </>
    )

  return (
    <form onSubmit={handleSubmit(onValid, onInvalid)}>
      <AnimatedContainer>
        <div className="space-y-4">
          {/* --- Personal --- */}
          <AnimatedSection>
            <Section
              key="profile"
              {...sectionOpenProps('personal')}
              title={
                <SectionTitle
                  icon={<UserIcon className="h-4 w-4" />}
                  title={t('form.sections.personal')}
                />
              }
            >
              <div className="grid grid-cols-1 gap-4 p-5 pt-2 lg:grid-cols-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-[150px_1fr_1fr] lg:col-span-2">
                  <Controller
                    control={control}
                    name="degree"
                    render={({ field }) => (
                      <DegreeField
                        value={field.value ?? undefined}
                        // Store `null` (not `undefined`) on clear — RHF treats
                        // `undefined` as "unset" and falls back to the field's
                        // defaultValue, so the old degree would reappear visually.
                        onChange={(v) => field.onChange(v ?? null)}
                        error={errors.degree?.message}
                      />
                    )}
                  />

                  <Labeled
                    label={t('form.labels.firstName')}
                    error={errors.first_name?.message}
                  >
                    <Input
                      placeholder={t('form.placeholders.firstName')}
                      className={invalidCls(errors.first_name)}
                      {...register('first_name', { validate: lenRule(2, 50) })}
                    />
                  </Labeled>

                  <Labeled
                    label={t('form.labels.lastName')}
                    error={errors.last_name?.message}
                  >
                    <Input
                      placeholder={t('form.placeholders.lastName')}
                      className={invalidCls(errors.last_name)}
                      {...register('last_name', { validate: lenRule(2, 50) })}
                    />
                  </Labeled>
                </div>

                <Controller
                  control={control}
                  name="sex"
                  render={({ field }) => (
                    <Labeled label={t('form.labels.sex')}>
                      <Combobox
                        searchable={false}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        placeholder={t('form.placeholders.sex')}
                        options={[
                          { label: t('form.sex.male'), value: 'MALE' },
                          { label: t('form.sex.female'), value: 'FEMALE' },
                          { label: t('form.sex.other'), value: 'OTHER' }
                        ]}
                      />
                    </Labeled>
                  )}
                />

                <Controller
                  control={control}
                  name="birth_date"
                  render={({ field }) => (
                    <Labeled
                      label={t('form.labels.birthDate')}
                      required
                      error={errors.birth_date?.message}
                    >
                      <DatePicker
                        max={TODAY_ISO}
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        invalid={!!errors.birth_date}
                      />
                    </Labeled>
                  )}
                />

                <Controller
                  control={control}
                  name="nationality"
                  render={({ field }) => (
                    <NationalityField
                      required
                      value={field.value ?? undefined}
                      onChange={field.onChange}
                      error={errors.nationality?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="country"
                  render={({ field }) => (
                    <CountryField
                      required
                      value={field.value ?? undefined}
                      onChange={field.onChange}
                      error={errors.country?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="id_number"
                  rules={{
                    pattern: {
                      value: ID_PATTERN,
                      message: t('form.validation.idNumberFormat')
                    }
                  }}
                  render={({ field }) => (
                    <Labeled
                      label={t('form.labels.idNumber')}
                      required={!passportRequired}
                      error={errors.id_number?.message}
                    >
                      <Input
                        placeholder="123456/1234"
                        maxLength={11}
                        className={invalidCls(errors.id_number)}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(formatIdNumber(e.target.value))
                        }
                        onBlur={field.onBlur}
                      />
                    </Labeled>
                  )}
                />

                <Controller
                  control={control}
                  name="passport_number"
                  rules={{
                    pattern: {
                      value: PASSPORT_PATTERN,
                      message: t('form.validation.passportNumberFormat')
                    }
                  }}
                  render={({ field }) => (
                    <Labeled
                      label={t('form.labels.passportNumber')}
                      required={passportRequired}
                      error={errors.passport_number?.message}
                    >
                      <Input
                        placeholder="AB0123456"
                        className={invalidCls(errors.passport_number)}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value.toUpperCase())
                        }
                        onBlur={field.onBlur}
                      />
                    </Labeled>
                  )}
                />

                <AccessControl role="ADMIN">
                  <Controller
                    control={control}
                    name="rating"
                    rules={{
                      min: { value: 0, message: t('form.validation.ratingRange') },
                      max: { value: 10, message: t('form.validation.ratingRange') }
                    }}
                    render={({ field }) => (
                      <Labeled
                        label={t('form.labels.rating')}
                        error={errors.rating?.message}
                      >
                        <Input
                          type="number"
                          min={0}
                          max={10}
                          className={invalidCls(errors.rating)}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ''
                                ? undefined
                                : Number(e.target.value)
                            )
                          }
                          onBlur={field.onBlur}
                        />
                      </Labeled>
                    )}
                  />
                </AccessControl>

                <div className="flex items-start gap-2.5 rounded-xl bg-ink/[0.03] px-4 py-3 lg:col-span-2">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <p className="m-0 text-sm text-muted-foreground">
                    {contractNote}
                  </p>
                </div>
              </div>
            </Section>
          </AnimatedSection>

          {/* --- Legal representative (under 18) --- */}
          {isUnder18 && (
            <AnimatedSection>
              <div className="mb-4 flex items-center gap-2.5 rounded-2xl border border-peach/40 bg-peach/15 px-4 py-3 text-sm font-medium text-warning">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {t('underageWarning')}
              </div>
              <Section
                key="legal_representative"
                {...sectionOpenProps('legal')}
                title={
                  <SectionTitle
                    title={t('form.sections.legalRepresentative')}
                    icon={<Users className="h-4 w-4" />}
                  />
                }
              >
                <div className="space-y-5 p-5 pt-2">
                  <div>
                    <h3 className="m-0 mb-3 font-display text-base font-bold text-ink">
                      {t('form.sections.personal')}
                    </h3>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[150px_1fr_1fr] lg:col-span-2">
                        <Controller
                          control={control}
                          // legal_representative is a free-form JSON blob; degree
                          // isn't in the generated type but is stored as-is.
                          name={'legal_representative.degree' as never}
                          render={({ field }) => (
                            <DegreeField
                              value={(field.value as string | undefined) ?? undefined}
                              // See note on the main degree field: clear to `null`
                              // so RHF doesn't fall back to the defaultValue.
                              onChange={(v) => field.onChange(v ?? null)}
                            />
                          )}
                        />

                        <Labeled
                          label={t('form.labels.firstName')}
                          required
                          error={
                            errors.legal_representative?.first_name?.message
                          }
                        >
                          <Input
                            placeholder={t('form.placeholders.firstName')}
                            className={invalidCls(
                              errors.legal_representative?.first_name
                            )}
                            {...register('legal_representative.first_name', {
                              required: t('form.validation.required'),
                              validate: lenRule(2, 50)
                            })}
                          />
                        </Labeled>

                        <Labeled
                          label={t('form.labels.lastName')}
                          required
                          error={errors.legal_representative?.last_name?.message}
                        >
                          <Input
                            placeholder={t('form.placeholders.lastName')}
                            className={invalidCls(
                              errors.legal_representative?.last_name
                            )}
                            {...register('legal_representative.last_name', {
                              required: t('form.validation.required'),
                              validate: lenRule(2, 50)
                            })}
                          />
                        </Labeled>
                      </div>

                      <Controller
                        control={control}
                        name="legal_representative.birth_date"
                        rules={{
                          required: t('form.validation.required'),
                          validate: (v) => {
                            if (!v) return true
                            const age = ageFromIso(v)
                            return (
                              (age !== null && age >= 18) ||
                              t('form.validation.legalRepAdult')
                            )
                          }
                        }}
                        render={({ field }) => (
                          <Labeled
                            label={t('form.labels.birthDate')}
                            required
                            error={
                              errors.legal_representative?.birth_date?.message
                            }
                          >
                            <DatePicker
                              max={ADULT_BIRTH_DATE_MAX_ISO}
                              value={field.value ?? undefined}
                              onChange={field.onChange}
                              invalid={
                                !!errors.legal_representative?.birth_date
                              }
                            />
                          </Labeled>
                        )}
                      />

                      <Controller
                        control={control}
                        name="legal_representative.nationality"
                        rules={{ required: t('form.validation.required') }}
                        render={({ field }) => (
                          <NationalityField
                            required
                            value={field.value ?? undefined}
                            onChange={field.onChange}
                            error={
                              errors.legal_representative?.nationality?.message
                            }
                          />
                        )}
                      />

                      <Controller
                        control={control}
                        name="legal_representative.country"
                        rules={{ required: t('form.validation.required') }}
                        render={({ field }) => (
                          <CountryField
                            required
                            value={field.value ?? undefined}
                            onChange={field.onChange}
                            error={errors.legal_representative?.country?.message}
                          />
                        )}
                      />

                      <Controller
                        control={control}
                        name="legal_representative.id_number"
                        rules={{
                          pattern: {
                            value: ID_PATTERN,
                            message: t('form.validation.idNumberFormat')
                          }
                        }}
                        render={({ field }) => (
                          <Labeled
                            label={t('form.labels.idNumber')}
                            required={!legalRepPassportRequired}
                            error={
                              errors.legal_representative?.id_number?.message
                            }
                          >
                            <Input
                              placeholder="123456/1234"
                              maxLength={11}
                              className={invalidCls(
                                errors.legal_representative?.id_number
                              )}
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(formatIdNumber(e.target.value))
                              }
                              onBlur={field.onBlur}
                            />
                          </Labeled>
                        )}
                      />

                      <Controller
                        control={control}
                        name="legal_representative.passport_number"
                        rules={{
                          pattern: {
                            value: PASSPORT_PATTERN,
                            message: t('form.validation.passportNumberFormat')
                          }
                        }}
                        render={({ field }) => (
                          <Labeled
                            label={t('form.labels.passportNumber')}
                            required={legalRepPassportRequired}
                            error={
                              errors.legal_representative?.passport_number
                                ?.message
                            }
                          >
                            <Input
                              placeholder="AB0123456"
                              className={invalidCls(
                                errors.legal_representative?.passport_number
                              )}
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(e.target.value.toUpperCase())
                              }
                              onBlur={field.onBlur}
                            />
                          </Labeled>
                        )}
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="m-0 mb-3 font-display text-base font-bold text-ink">
                      {t('form.sections.contact')}
                    </h3>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <Labeled
                        label={t('form.labels.email')}
                        required
                        error={errors.legal_representative?.email?.message}
                      >
                        <Input
                          placeholder="email@example.com"
                          className={invalidCls(
                            errors.legal_representative?.email
                          )}
                          {...register('legal_representative.email', {
                            required: t('form.validation.required'),
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: t('form.validation.invalidEmail')
                            },
                            validate: (v) => {
                              const own = getValues('email') ?? data?.email
                              return (
                                !v ||
                                !own ||
                                v.trim().toLowerCase() !==
                                  own.trim().toLowerCase() ||
                                t('form.validation.legalRepEmailSameAsUser')
                              )
                            }
                          })}
                        />
                      </Labeled>

                      <Controller
                        control={control}
                        name="legal_representative.phone"
                        rules={{
                          required: t('form.validation.required'),
                          pattern: {
                            value: PHONE_PATTERN,
                            message: t('form.validation.invalidPhone')
                          }
                        }}
                        render={({ field }) => (
                          <Labeled
                            label={t('form.labels.phone')}
                            required
                            error={errors.legal_representative?.phone?.message}
                          >
                            <PhoneInput
                              value={field.value ?? undefined}
                              onChange={field.onChange}
                              invalid={!!errors.legal_representative?.phone}
                            />
                          </Labeled>
                        )}
                      />

                      <Labeled
                        label={t('form.labels.street')}
                        required
                        error={errors.legal_representative?.street?.message}
                      >
                        <Input
                          placeholder={t('form.placeholders.street')}
                          className={invalidCls(
                            errors.legal_representative?.street
                          )}
                          {...register('legal_representative.street', {
                            required: t('form.validation.required'),
                            validate: lenRule(2, 100)
                          })}
                        />
                      </Labeled>

                      <Labeled label={t('form.labels.address_number')}>
                        <Input
                          placeholder={t('form.placeholders.address_number')}
                          {...register('legal_representative.address_number')}
                        />
                      </Labeled>

                      <Labeled
                        label={t('form.labels.address_orientation_number')}
                        required
                        error={
                          errors.legal_representative?.address_orientation_number
                            ?.message
                        }
                      >
                        <Input
                          placeholder={t(
                            'form.placeholders.address_orientation_number'
                          )}
                          className={invalidCls(
                            errors.legal_representative
                              ?.address_orientation_number
                          )}
                          {...register(
                            'legal_representative.address_orientation_number',
                            { required: t('form.validation.required') }
                          )}
                        />
                      </Labeled>

                      <Labeled
                        label={t('form.labels.zip')}
                        required
                        error={errors.legal_representative?.zip?.message}
                      >
                        <Input
                          placeholder="XXXXX"
                          className={invalidCls(errors.legal_representative?.zip)}
                          {...register('legal_representative.zip', {
                            required: t('form.validation.required'),
                            setValueAs: (v) => v?.replace(/\s+/g, ''),
                            pattern: {
                              value: ZIP_PATTERN,
                              message: t('form.validation.invalidZip')
                            }
                          })}
                        />
                      </Labeled>

                      <Labeled
                        label={t('form.labels.city')}
                        required
                        error={errors.legal_representative?.city?.message}
                      >
                        <Input
                          placeholder={t('form.placeholders.city')}
                          className={invalidCls(
                            errors.legal_representative?.city
                          )}
                          {...register('legal_representative.city', {
                            required: t('form.validation.required'),
                            validate: lenRule(2, 100)
                          })}
                        />
                      </Labeled>
                    </div>
                  </div>
                </div>
              </Section>
            </AnimatedSection>
          )}

          {/* --- Contact --- */}
          <AnimatedSection>
            <Section
              key="contact"
              {...sectionOpenProps('contact')}
              title={
                <SectionTitle
                  title={t('form.sections.contact')}
                  icon={<Contact className="h-4 w-4" />}
                />
              }
            >
              <div className="grid grid-cols-1 gap-4 p-5 pt-2 lg:grid-cols-2">
                <Labeled label={t('form.labels.email')}>
                  <Input
                    placeholder="email@example.com"
                    disabled
                    {...register('email')}
                  />
                </Labeled>

                <Controller
                  control={control}
                  name="phone"
                  rules={{
                    pattern: {
                      value: PHONE_PATTERN,
                      message: t('form.validation.invalidPhone')
                    }
                  }}
                  render={({ field }) => (
                    <Labeled
                      label={t('form.labels.phone')}
                      required
                      error={errors.phone?.message}
                    >
                      <PhoneInput
                        value={field.value ?? undefined}
                        onChange={field.onChange}
                        invalid={!!errors.phone}
                      />
                    </Labeled>
                  )}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:col-span-2">
                  <Labeled label={t('form.labels.street')} required>
                    <Input
                      placeholder={t('form.placeholders.street')}
                      {...register('street')}
                    />
                  </Labeled>

                  <Labeled label={t('form.labels.address_number')}>
                    <Input
                      placeholder={t('form.placeholders.address_number')}
                      {...register('address_number')}
                    />
                  </Labeled>

                  <Labeled
                    label={t('form.labels.address_orientation_number')}
                    required
                    error={errors.address_orientation_number?.message}
                  >
                    <Input
                      placeholder={t(
                        'form.placeholders.address_orientation_number'
                      )}
                      className={invalidCls(errors.address_orientation_number)}
                      {...register('address_orientation_number')}
                    />
                  </Labeled>
                </div>

                <Labeled
                  label={t('form.labels.zip')}
                  required
                  error={errors.zip?.message}
                >
                  <Input
                    placeholder="XXXXX"
                    className={invalidCls(errors.zip)}
                    {...register('zip', {
                      setValueAs: (v) => v?.replace(/\s+/g, ''),
                      pattern: {
                        value: ZIP_PATTERN,
                        message: t('form.validation.invalidZip')
                      }
                    })}
                  />
                </Labeled>

                <Labeled label={t('form.labels.city')} required>
                  <Input
                    placeholder={t('form.placeholders.city')}
                    {...register('city')}
                  />
                </Labeled>

                {SHOW_SOCIALS && (
                  <>
                    <Labeled label={t('form.labels.facebook')}>
                      <Input
                        placeholder="https://facebook.com/profile"
                        {...register('facebook')}
                      />
                    </Labeled>
                    <Labeled label={t('form.labels.instagram')}>
                      <Input
                        placeholder="https://instagram.com/profile"
                        {...register('instagram')}
                      />
                    </Labeled>
                    <Labeled label={t('form.labels.linkedin')}>
                      <Input
                        placeholder="https://linkedin.com/in/profile"
                        {...register('linkedin')}
                      />
                    </Labeled>
                  </>
                )}

                <div className="flex items-start gap-2.5 rounded-xl bg-ink/[0.03] px-4 py-3 lg:col-span-2">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <p className="m-0 text-sm text-muted-foreground">
                    {contractNote}
                  </p>
                </div>
              </div>
            </Section>
          </AnimatedSection>

          {/* --- Business --- */}
          {!hideEmployment && (
          <AnimatedSection>
            <Section
              key="business"
              {...sectionOpenProps('business')}
              title={
                <SectionTitle
                  title={t('form.sections.business')}
                  icon={<Briefcase className="h-4 w-4" />}
                />
              }
            >
              <div className="grid grid-cols-1 gap-4 p-5 pt-2 lg:grid-cols-2">
                <Controller
                  control={control}
                  name="job"
                  render={({ field }) => (
                    <JobField
                      value={field.value ?? undefined}
                      onChange={field.onChange}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="income"
                  render={({ field }) => (
                    <Labeled label={t('form.labels.income')}>
                      <Input
                        type="number"
                        placeholder={t('form.placeholders.income')}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ''
                              ? undefined
                              : Number(e.target.value)
                          )
                        }
                        onBlur={field.onBlur}
                      />
                    </Labeled>
                  )}
                />
              </div>
            </Section>
          </AnimatedSection>
          )}
        </div>
      </AnimatedContainer>
      {showFloatingSave && <FloatingSave discard={discardChanges} />}
    </form>
  )
}
